import React from 'react';
import {
  Banner,
  Button,
  Card,
  CardContent,
  CardHeader,
  Flex,
  Heading,
  Select,
  Switch,
  Text,
  useToast,
} from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { ApiError } from '../../../lib/api-error';
import {
  useFilingSettings,
  useTicketTaxonomy,
  useUpdateFilingSettings,
  type TicketFilingSettings,
} from '../../../hooks/useTicketingProviders';
import { describeMissingTaxonomy, findMissingTaxonomyValues } from '../taxonomy';
import { FilingTaxonomyFields } from './FilingTaxonomyFields';

const PROVIDER_ID = 'github';
const HIDDEN_ERROR_CODES = ['GITHUB_ISSUES_NOT_ENABLED', 'GITHUB_TICKETING_NOT_CONNECTED'];

type GitHubFilingSettings = { trackerRepo: string | null; availableRepos: string[] };

function FilingSettingsForm({
  settings,
  github,
}: {
  settings: TicketFilingSettings;
  github: GitHubFilingSettings;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';
  const update = useUpdateFilingSettings(PROVIDER_ID);

  const { trackerRepo: savedRepo, availableRepos } = github;
  const [trackerRepo, setTrackerRepo] = React.useState<string | null>(savedRepo);
  const [type, setType] = React.useState<string | null>(settings.defaultType);
  const [labels, setLabels] = React.useState<string[]>(settings.defaultLabels);
  const [autoFile, setAutoFile] = React.useState(settings.autoFile);

  // The pickers always show the SAVED repo's taxonomy; a different repo loads after saving.
  const taxonomyQuery = useTicketTaxonomy(PROVIDER_ID, savedRepo, savedRepo !== null);
  const taxonomy = taxonomyQuery.data?.data;
  const repoChanged = trackerRepo !== savedRepo;
  const missing = findMissingTaxonomyValues(taxonomy, settings.defaultType, settings.defaultLabels);
  const needsAttention = missing.type !== null || missing.labels.length > 0;

  const isDirty =
    repoChanged ||
    type !== settings.defaultType ||
    autoFile !== settings.autoFile ||
    labels.length !== settings.defaultLabels.length ||
    labels.some((label, index) => label !== settings.defaultLabels[index]);

  const save = () => {
    update.mutate(
      {
        defaultType: type,
        defaultLabels: labels,
        autoFile,
        extensions: { github: { trackerRepo } },
      },
      { onSuccess: () => toast.success('Filing settings saved') }
    );
  };

  return (
    <Flex direction="column" gap="lg">
      {update.error && <Banner variant="error">{update.error.message}</Banner>}
      {taxonomyQuery.error && <Banner variant="error">{taxonomyQuery.error.message}</Banner>}
      {needsAttention && (
        <Banner variant="warning">
          Needs attention: the saved default{' '}
          {describeMissingTaxonomy(missing.type, missing.labels)}{' '}
          no longer exist in {savedRepo}. Tickets are filed without them until you change them.
        </Banner>
      )}
      <Select
        label="Tracker repository"
        selectedKey={trackerRepo}
        items={(savedRepo && !availableRepos.includes(savedRepo)
          ? [savedRepo, ...availableRepos]
          : availableRepos
        ).map((repo) => ({ id: repo, label: repo }))}
        placeholder="Select a repository..."
        isDisabled={!isAdmin}
        description="Where Cofactor files tickets."
        onSelectionChange={(key) => {
          const next = key ? String(key) : null;
          setTrackerRepo(next);
          if (next !== savedRepo) {
            setType(null);
            setLabels([]);
          } else {
            setType(settings.defaultType);
            setLabels(settings.defaultLabels);
          }
        }}
      />
      {repoChanged && (
        <Text size="sm" className="text-muted-foreground">
          Save to load this repository's labels and types, then choose defaults.
        </Text>
      )}
      <FilingTaxonomyFields
        taxonomy={repoChanged ? undefined : taxonomy}
        type={type}
        labels={labels}
        onTypeChange={setType}
        onLabelsChange={setLabels}
        isDisabled={!isAdmin || savedRepo === null}
      />
      <div>
        <Switch
          isSelected={autoFile}
          onChange={setAutoFile}
          isDisabled={!isAdmin || savedRepo === null}
          label="File a ticket automatically for each new issue a failure diagnosis confirms as a product bug"
        />
        {savedRepo === null && (
          <Text size="sm" className="text-muted-foreground mt-1">
            Save a tracker repository to turn this on.
          </Text>
        )}
      </div>
      {isAdmin ? (
        <Flex gap="sm">
          <Button variant="primary" size="sm" onPress={save} isDisabled={!isDirty || update.isPending}>
            {update.isPending ? 'Saving...' : 'Save'}
          </Button>
        </Flex>
      ) : (
        <Text size="sm" className="text-muted-foreground">
          Only admins can change filing settings.
        </Text>
      )}
    </Flex>
  );
}

/** GitHub ticket filing defaults. Renders nothing when GitHub issues are off or not connected. */
export function FilingSettingsSection() {
  const settingsQuery = useFilingSettings(PROVIDER_ID, { enabled: true, forEditing: true });
  const error = settingsQuery.error;
  const github = settingsQuery.data?.data.extensions?.github;
  if (error instanceof ApiError && error.code && HIDDEN_ERROR_CODES.includes(error.code)) {
    return null;
  }
  if (settingsQuery.isLoading) return null;

  return (
    <Card padding="lg">
      <CardHeader>
        <Heading level={3}>Filing</Heading>
        <Text size="sm" className="text-muted-foreground mt-1">
          Defaults for GitHub tickets filed from an issue, by hand or automatically.
        </Text>
      </CardHeader>
      <CardContent>
        {error && <Banner variant="error">{error.message}</Banner>}
        {settingsQuery.data && !github && (
          <Banner variant="error">The server returned no GitHub filing settings.</Banner>
        )}
        {settingsQuery.data && github && (
          <FilingSettingsForm
            key={JSON.stringify([
              github.trackerRepo,
              settingsQuery.data.data.defaultType,
              settingsQuery.data.data.defaultLabels,
              settingsQuery.data.data.autoFile,
            ])}
            settings={settingsQuery.data.data}
            github={github}
          />
        )}
      </CardContent>
    </Card>
  );
}
